import { createContext, memo, useContext, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock, type CodeCopyLabels } from './CodeBlock.js';

/** `load`: images render as `<img>`. `block`: no request — a link (alt + URL) the reader may click. */
type MarkdownImagePolicy = 'load' | 'block';

interface MarkdownContentProps {
  content: string;
  className?: string;
  /** Render a single newline as a line break (GitHub-comment style) instead of a space. */
  softBreaks?: boolean;
  /** Code-block copy button words, in the calling surface's language. */
  copyLabels: CodeCopyLabels;
  /** Remote images; `block` for text the platform did not shape (silent GET = exfil channel). */
  imagePolicy?: MarkdownImagePolicy;
  /** Inline constructs only (bold, code, link…): `---`, `1.`, `# x`, `[a]: url` stay text. */
  inlineOnly?: boolean;
}

interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
}

/** mdast pass: split `text` nodes on newlines into text + `break` nodes (code nodes untouched). */
function splitSoftBreaks(node: MdNode): void {
  if (node.children === undefined) return;
  node.children = node.children.flatMap((child): MdNode[] => {
    if (child.type === 'text' && child.value !== undefined && child.value.includes('\n')) {
      return child.value
        .split(/\r?\n/)
        .flatMap((part, i): MdNode[] =>
          i === 0 ? [{ type: 'text', value: part }] : [{ type: 'break' }, { type: 'text', value: part }],
        );
    }
    splitSoftBreaks(child);
    return [child];
  });
}

function remarkSoftBreaks() {
  return (tree: unknown) => splitSoftBreaks(tree as MdNode);
}

/** micromark constructs that open a BLOCK; disabled under `inlineOnly`. */
const BLOCK_CONSTRUCTS = [
  'blockQuote',
  'codeFenced',
  'codeIndented',
  'definition',
  'gfmFootnoteDefinition',
  'headingAtx',
  'htmlFlow',
  'list',
  'setextUnderline',
  'table',
  'thematicBreak',
];

function remarkInlineOnly(this: { data(): unknown }) {
  const data = this.data() as { micromarkExtensions?: unknown[] };
  (data.micromarkExtensions ??= []).push({ disable: { null: BLOCK_CONSTRUCTS } });
}

interface HastNode {
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

// mdast-util-to-hast hard-codes this id (clobberPrefix does not reach it).
const FOOTNOTE_LABEL_ID = 'footnote-label';

function scopeFootnoteLabel(node: HastNode, prefix: string): void {
  const props = node.properties;
  if (props !== undefined) {
    if (props.id === FOOTNOTE_LABEL_ID) props.id = prefix + FOOTNOTE_LABEL_ID;
    if (Array.isArray(props.ariaDescribedBy)) {
      props.ariaDescribedBy = props.ariaDescribedBy.map((v: unknown) =>
        v === FOOTNOTE_LABEL_ID ? prefix + FOOTNOTE_LABEL_ID : v,
      );
    }
  }
  node.children?.forEach((child) => scopeFootnoteLabel(child, prefix));
}

function rehypeScopeFootnoteLabel(options: { prefix: string }) {
  return (tree: unknown) => scopeFootnoteLabel(tree as HastNode, options.prefix);
}

/** True under a rendered `<a>` — a blocked image there must not nest a second anchor. */
const InsideLink = createContext(false);

function BlockedImage({ src, alt }: { src?: string | Blob; alt?: string }) {
  const insideLink = useContext(InsideLink);
  const url = typeof src === 'string' ? src : '';
  const text = `[image${alt ? `: ${alt}` : ''}]${url === '' ? '' : ` ${url}`}`;
  if (insideLink || url === '') return <span data-md-image="blocked">{text}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-md-image="blocked"
      className="underline opacity-80 hover:opacity-100"
    >
      {text}
    </a>
  );
}

// Memoised: re-parsing is the cost, and callers pass stable props (constant labels/classes).
export const MarkdownContent = memo(function MarkdownContent({
  content,
  className,
  softBreaks = false,
  copyLabels,
  imagePolicy = 'load',
  inlineOnly = false,
}: MarkdownContentProps) {
  // Per-block footnote ids: two blocks on one page must not share `fn-1`.
  const prefix = `md-${useId().replace(/[^A-Za-z0-9_-]/g, '')}-`;
  const remarkPlugins = [
    remarkGfm,
    ...(softBreaks ? [remarkSoftBreaks] : []),
    ...(inlineOnly ? [remarkInlineOnly] : []),
  ];
  return (
    <div className={className}>
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      remarkRehypeOptions={{ clobberPrefix: prefix }}
      rehypePlugins={[[rehypeScopeFootnoteLabel, { prefix }]]}
      components={{
        ...(imagePolicy === 'block' ? { img: BlockedImage } : {}),
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>,
        ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>,
        // `id` is forwarded for footnotes only (markdown mints no other id; prefixed above).
        li: ({ id, children }) => <li id={id} className="mb-0.5">{children}</li>,
        h1: ({ children }) => <h1 className="mb-2 text-base font-bold">{children}</h1>,
        h2: ({ id, children }) => <h2 id={id} className="mb-1.5 text-sm font-bold">{children}</h2>,
        h3: ({ children }) => <h3 className="mb-1 text-sm font-semibold">{children}</h3>,
        code: ({ className: codeClassName, children, ...props }) => {
          // Detect block code (has language class) vs inline
          const match = /language-(\w+)/.exec(codeClassName || '');
          if (match) {
            // Block code — will be wrapped by <pre> override below
            return (
              <code data-language={match[1]} className={codeClassName} {...props}>
                {children}
              </code>
            );
          }
          return (
            <code className="rounded bg-black/10 px-1 py-0.5 text-xs font-mono dark:bg-white/10" {...props}>
              {children}
            </code>
          );
        },
        pre: ({ children }) => {
          // Extract language and text from the code child
          const child = children as React.ReactElement<{ 'data-language'?: string; children?: React.ReactNode }>;
          if (child?.props?.['data-language']) {
            const lang = child.props['data-language'];
            const text = String(child.props.children ?? '').replace(/\n$/, '');
            return <CodeBlock code={text} language={lang} labels={copyLabels} />;
          }
          // Fallback: plain pre without language
          const text = String(
            (child?.props?.children ?? children) || ''
          ).replace(/\n$/, '');
          return <CodeBlock code={text} labels={copyLabels} />;
        },
        table: ({ children }) => (
          <div className="mb-2 overflow-auto last:mb-0">
            <table className="min-w-full text-xs border-collapse">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="border-b border-current/10">{children}</thead>,
        th: ({ children }) => <th className="px-2 py-1 text-left font-semibold">{children}</th>,
        td: ({ children }) => <td className="px-2 py-1 border-t border-current/5">{children}</td>,
        a: ({ href, id, children, 'aria-describedby': describedBy, 'aria-label': label }) => (
          <a
            href={href}
            id={id}
            aria-describedby={describedBy}
            aria-label={label}
            // An in-page (footnote) jump stays in this tab.
            {...(href?.startsWith('#') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
            className="underline opacity-80 hover:opacity-100"
          >
            <InsideLink.Provider value>{children}</InsideLink.Provider>
          </a>
        ),
        blockquote: ({ children }) => (
          <blockquote className="mb-2 border-l-2 border-current/20 pl-3 opacity-80 last:mb-0">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-2 border-current/10" />,
      }}
    >
      {content}
    </ReactMarkdown>
    </div>
  );
});
